import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { PostAxios } from "../../helpers/axiosHelpers";
import { toast } from "sonner";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  // Validation Schema
  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[a-z]/, "Must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Must contain at least one uppercase letter")
      .matches(/[0-9]/, "Must contain at least one number")
      .matches(/[!@#$%^&*(),.?":{}|<>]/, "Must contain at least one symbol")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const res = await PostAxios("/auth/reset-password", {
          token,
          email,
          newPassword: values.password,
        });

        if (res.success) {
          setIsSuccess(true);
          toast.success("Password updated successfully!");
        } else {
          toast.error(res.msg || "Invalid or expired token.");
        }
      } catch (error) {
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] p-10 shadow-2xl text-center space-y-6 animate-in fade-in zoom-in-95">
          <div className="w-20 h-20 bg-accent-green/10 rounded-full flex items-center justify-center text-accent-green mx-auto">
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-serif-brand font-black text-slate-900">
            Success!
          </h2>
          <p className="text-slate-500 font-medium">
            Your password has been reset. You can now log in.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full bg-subidha-blue hover:bg-slate-900 text-white py-5 rounded-2xl font-black transition-all"
          >
            BACK TO LOGIN
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 pt-32">
      <div className="w-full max-w-md bg-white rounded-[2.5rem] p-10 shadow-2xl border border-slate-100 space-y-8">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-subidha-blue mx-auto">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-serif-brand font-black text-slate-900">
            New Password
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            Resetting for{" "}
            <span className="text-subidha-blue underline">{email}</span>
          </p>
        </div>

        <form className="space-y-5" onSubmit={formik.handleSubmit}>
          {/* New Password */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
              New Password
            </label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all ${
                  formik.touched.password && formik.errors.password
                    ? "border-red-500 ring-4 ring-red-500/10"
                    : "border-slate-200 focus:ring-4 focus:ring-accent-green/20"
                }`}
                placeholder="••••••••"
                {...formik.getFieldProps("password")}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-subidha-blue"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {formik.touched.password && formik.errors.password && (
              <p className="text-[11px] text-red-500 font-bold px-1">
                {formik.errors.password}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
              Confirm New Password
            </label>
            <input
              name="confirmPassword"
              type="password"
              className={`w-full px-5 py-4 bg-slate-50 border rounded-2xl outline-none transition-all ${
                formik.touched.confirmPassword && formik.errors.confirmPassword
                  ? "border-red-500 ring-4 ring-red-500/10"
                  : "border-slate-200 focus:ring-4 focus:ring-accent-green/20"
              }`}
              placeholder="••••••••"
              {...formik.getFieldProps("confirmPassword")}
            />
            {formik.touched.confirmPassword &&
              formik.errors.confirmPassword && (
                <p className="text-[11px] text-red-500 font-bold px-1">
                  {formik.errors.confirmPassword}
                </p>
              )}
          </div>

          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="w-full bg-subidha-blue hover:bg-slate-900 text-white py-5 rounded-2xl font-black transition-all disabled:opacity-50 flex items-center justify-center"
          >
            {formik.isSubmitting ? (
              <Loader2 className="animate-spin mr-2" />
            ) : (
              "UPDATE PASSWORD"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
